import { orderFormSpace } from 'feature/productOrder/namespaces/orderFormSpace'
import { convertBytesToString } from 'helpers/convertBytesToString'
import { getApiErrorMessage } from 'helpers/getApiErrorMessage'
import { useSetNotice } from 'hooks/useSetNotice'
import {
  ChangeEvent,
  Dispatch,
  DragEvent,
  RefObject,
  SetStateAction,
} from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Severity } from 'widget/Notifications'

import { TDragDropInput } from '../types'

import { isUploadFileError } from './fileChecking'

type TUseHandles = {
  props: TDragDropInput
  inputRef: RefObject<HTMLInputElement>
  setDragActive: Dispatch<SetStateAction<boolean>>
}
// обработчики взаимодействия с файлами
export const useHandlers = ({
  props,
  inputRef,
  setDragActive,
}: TUseHandles) => {
  const setNotice = useSetNotice()

  const {
    fileList,
    setFileList,
    loadFileToApi,
    limit,
    onRemoveFile,
    validateFile,
  } = props

  const fileValidator = validateFile ?? isUploadFileError

  const handleFiles = async (files: FileList) => {
    const selectedFiles = Array.from(files)
    let thumbnailsList: orderFormSpace.Images[] = []

    for (let file of selectedFiles) {
      // проверка количества файлов
      if (fileList.length + thumbnailsList.length >= limit) {
        setNotice('Превышен лимит количества файлов (5 шт)', Severity.warning)
        break
      }

      const isFileCheckingError = fileValidator(file)

      if (isFileCheckingError.isError) {
        setNotice(isFileCheckingError.message, Severity.warning)
        return
      }

      const generateId = uuidv4()
      // загрузка файла на сервер
      if (loadFileToApi)
        loadFileToApi(file)
          .then(data => {
            setFileList(prev =>
              prev.map(item =>
                item.id === generateId
                  ? {
                      ...item,
                      serverUid: data.uid,
                      previewUrl: data.previewUrl,
                    }
                  : item,
              ),
            )
          })
          .catch((error: unknown) => {
            setFileList(prev => prev.filter(item => item.id !== generateId))
            setNotice(
              getApiErrorMessage(error, 'Ошибка загрузки файла'),
              Severity.error,
            )
          })

      thumbnailsList.push({
        id: generateId,
        name: file.name,
        serverUid: null,
        previewUrl: null,
        size: convertBytesToString(file.size),
        mimeType: file.type,
      })
    }

    if (fileList) {
      setFileList(prevThumbnails => {
        return [...prevThumbnails, ...thumbnailsList]
      })
    } else {
      setFileList(thumbnailsList)
    }

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files)
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()

    const { files } = e.target
    if (files) {
      handleFiles(files)
    }
  }

  const handleDrag = (e: DragEvent<HTMLDivElement>, isActive: boolean) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(isActive)
  }

  const removeThumbnail = async (index: number) => {
    const updatedThumbnails = [...fileList]
    const removedItem = updatedThumbnails[index]

    if (!removedItem) return

    if (onRemoveFile) {
      try {
        await onRemoveFile(removedItem)
      } catch (error) {
        setNotice('Ошибка удаления файла', Severity.error)
        return
      }
    }

    updatedThumbnails.splice(index, 1)

    if (inputRef.current) {
      inputRef.current.value = ''
    }
    setFileList(updatedThumbnails)
  }

  return { handleFiles, handleDrop, handleChange, handleDrag, removeThumbnail }
}
